import { useState } from 'react';
import { Badge, Box, IconButton, Tooltip, Typography } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import RarDelEstablecimientoDialog, { clasificarRars } from './RarDelEstablecimientoDialog';
import { AVISO_AMBAR, AVISO_NEUTRO } from './VigenciaBadge';

import { CuitPresentacionSvcc } from '../hooks/usePresentacionesSVCC';

/**
 * De qué año calendario habla la fila.
 *
 * La DDJJ de cancerígenos es anual, cubre el año calendario anterior y vence el
 * 1 de abril del siguiente. La grilla mostraba el estado del trámite sin decir
 * nunca de qué período era, y un CUIT con informes de dos años se veía como una
 * sola fila con un solo estado.
 *
 * Tres situaciones, y la celda las distingue porque no valen lo mismo:
 *
 * - **Declarado**: alguien eligió el año al crear el informe.
 * - **Inferido**: lo dedujo el backend para los informes anteriores a que el
 *   período existiera como dato. Puede no coincidir con lo que la SRT registró,
 *   así que se avisa en vez de mostrarlo como un hecho.
 * - **Sin período**: nadie lo declaró y el informe nunca se envió. No es un
 *   dato faltante que haya que esconder: es lo que hay que resolver antes de
 *   poder presentar.
 */

/**
 * Lo que la SRT va a recibir como nómina, y con qué RAR se puede comparar.
 *
 * Esta celda decía "de qué RAR salió la nómina". Esa pregunta no tiene
 * respuesta en el modelo: el informe SVCC apunta a la nómina VIVA del
 * establecimiento (`empleados_nomina`), no a un RAR, y el Anexo III se arma
 * leyendo esa misma tabla viva al momento de enviar. La grilla deducía un RAR
 * por fecha y lo mostraba como un hecho; en el caso que destapó esto, afirmaba
 * "Nómina del RAR 765" sobre un establecimiento cuya nómina viva estaba vacía.
 *
 * Ahora dice lo verificable, en el orden en que importa:
 *
 * 1. **Cuántos trabajadores van a viajar** — misma condición que el envío.
 * 2. **Cuántos expuestos declara el informe.**
 * 3. Un aviso cuando no coinciden: nómina vacía, o expuestos que se declaran
 *    y no viajan porque su fila viva ya pertenece a otro establecimiento.
 *
 * Los RAR del establecimiento quedan en el modal, para abrirlos y comparar a
 * ojo. Contexto, no "el origen".
 *
 * **Qué RAR cuenta.** Un RAR es la nómina el día que se presentó, y queda
 * vigente hasta que otro lo reemplaza; el backend manda su año (`anio`). El
 * SVCC del período YYYY declara el año calendario YYYY, así que los RAR que lo
 * describen son los presentados ese año o, si no hubo, el vigente al cierre
 * (el más reciente hasta el 31/12 — regla del cliente). Los posteriores no:
 * un RAR de enero muestra a quien ingresó en enero. La celda cuenta ésos; el
 * resto sigue en el modal, aparte. Un botón "Ver 5 RAR" debajo de "2024" que
 * contara los de 2023 y 2025 decía que eran de 2024.
 */
/**
 * El aviso de RAR del pie de la celda es un ícono con contador y tooltip, no
 * un botón ni un chip a lo ancho: la celda es la más cargada de la grilla y un
 * bloque de 250px competía con el dato. El contador dice cuántos; el tooltip,
 * qué. En ámbar cuando hay algo que mirar.
 */
/*
 * Misma familia que los chips de la grilla (`AVISO_NEUTRO` / `AVISO_AMBAR`):
 * ícono, borde y badge del mismo par. Antes el badge salía en el `primary` del
 * tema —negro en backoffice— y el aviso en el naranja de MUI: tres tonos que
 * no tenían que ver entre sí ni con el resto de la fila.
 */
const TONO_ICONO = {
    neutro: { icono: '#5F6B7A', borde: AVISO_NEUTRO.borderColor, badge: '#5F6B7A' },
    aviso: { icono: AVISO_AMBAR.textColor, borde: AVISO_AMBAR.borderColor, badge: '#B8770A' }
};

const ESTILO_ICONO_CELDA = (aviso: boolean) => {
    const tono = aviso ? TONO_ICONO.aviso : TONO_ICONO.neutro;
    return {
        p: 0.75,
        border: '1px solid',
        borderColor: tono.borde,
        borderRadius: 1.5,
        color: tono.icono,
        backgroundColor: aviso ? AVISO_AMBAR.backgorundColor : '#fff',
        /*
         * El badge en el vértice de la caja, con número centrado y un anillo
         * blanco para despegarlo del borde. A 24px el número quedaba pisado por
         * el trazo del ícono.
         */
        '& .MuiBadge-badge': {
            backgroundColor: tono.badge,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            height: 18,
            minWidth: 18,
            px: 0.5,
            // Los dígitos no tienen descendente y el cajón del texto sí: la tinta
            // queda ~1px arriba del centro. Se compensa con el padding.
            pt: '1px',
            lineHeight: 1,
            top: -2,
            right: -2,
            boxShadow: '0 0 0 2px #fff'
        }
    };
};

const NominaCelda = ({
    cuit,
    anioPeriodo,
    fila
}: {
    cuit: string;
    anioPeriodo: number | null;
    fila: Pick<
        CuitPresentacionSvcc,
        'nomina' | 'rarsDelEstablecimiento' | 'establecimientosConRar' | 'cantidadEstablecimientos'
    >;
}) => {
    const [modalAbierto, setModalAbierto] = useState(false);
    const rars = fila.rarsDelEstablecimiento ?? [];
    /*
     * Sin `anio` en los RAR (backend anterior) no se filtra nada: si no, TODA
     * fila diría "Sin RAR de 2025" por un dato que no llegó, el mismo
     * falso positivo que "Nómina sin datos" evita más abajo.
     */
    const conAnio = rars.length > 0 && rars.every((rar) => typeof rar.anio === 'number');
    const filtrarPorPeriodo = anioPeriodo != null && conAnio;
    const { delPeriodo, posteriores, sinRarAlCierre } = clasificarRars(
        rars,
        filtrarPorPeriodo ? anioPeriodo : null
    );

    /*
     * Sin `nomina` no hay nada que afirmar. Pasa con un backend anterior a este
     * cambio —el Front se publica aparte y puede llegar primero— y con los mocks
     * parciales de los tests. Rellenar con ceros mostraba "⚠ 0 trabajadores
     * para enviar": un falso positivo que manda al operador a investigar una
     * nómina que no está vacía, sólo no llegó.
     */
    if (!fila.nomina) {
        return (
            <Typography
                variant="body2"
                data-testid="nomina-sin-datos"
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
                Nómina sin datos
            </Typography>
        );
    }
    const nomina = fila.nomina;

    const sinTrabajadores = nomina.trabajadoresParaEnviar === 0;
    const hayFueraDeNomina = nomina.expuestosFueraDeNomina > 0;
    const ajenos = nomina.expuestosDeOtroCuit?.length ?? 0;
    const sinAsignar = nomina.trabajadoresSinEstablecimiento ?? 0;
    const conAviso = sinTrabajadores || hayFueraDeNomina;
    /*
     * Rojo, no ámbar, cuando hay un trabajador de OTRO CUIT: no es un dato para
     * revisar, es un informe que no puede salir así.
     */
    const colorAviso = ajenos > 0 ? 'error.main' : 'warning.dark';

    // Sin ninguno que describa el período el botón cuenta los demás: abre igual y
    // el modal dice que son posteriores.
    const paraContar = delPeriodo.length > 0 ? delPeriodo : rars;
    /*
     * El contador es COBERTURA del lote —establecimientos con algún RAR sobre
     * establecimientos con informe—, no cantidad de PDFs. "Ver 5 RAR" bajo un
     * lote de 2 establecimientos no decía si alguno quedaba sin RAR, que es lo
     * que el operador tiene que saber antes de enviar. El matiz del período
     * (si esos RAR son del año o posteriores) sigue en el tooltip y en el
     * modal. Con un backend anterior la cobertura no viene y se cae a contar
     * los RAR del período, como antes.
     */
    const total = fila.cantidadEstablecimientos ?? 0;
    const conRar =
        typeof fila.establecimientosConRar === 'number' ? fila.establecimientosConRar : null;
    const sinCobertura = conRar !== null && conRar < total;
    const contador = conRar === null ? paraContar.length : `${conRar}/${total}`;
    const sinNingunRar = conRar === null ? paraContar.length === 0 : conRar === 0;
    const etiquetaBoton =
        conRar !== null
            ? sinNingunRar
                ? `Ver nómina · ningún establecimiento con RAR`
                : `Ver RAR · ${conRar} de ${total} ${
                      total === 1 ? 'establecimiento' : 'establecimientos'
                  } con RAR`
            : paraContar.length === 0
              ? 'Ver nómina'
              : paraContar.length === 1
                ? 'Ver RAR'
                : `Ver ${paraContar.length} RAR`;
    // "6 posteriores" y no sólo "sin RAR": hay RAR, y son los que cargaron la nómina.
    const avisoRar = sinRarAlCierre
        ? `Sin RAR de ${anioPeriodo} · ${posteriores.length} ${
              posteriores.length === 1 ? 'posterior' : 'posteriores'
          }`
        : null;
    // Una frase por situación: qué hay y qué falta. Sin "abre…": el ícono ya
    // es un botón, y la frase de más tapaba el dato.
    const faltantes = total - (conRar ?? 0);
    const tooltipRar = sinNingunRar
        ? 'Ningún establecimiento tiene RAR presentado.'
        : sinCobertura
          ? `${conRar} de ${total} establecimientos tienen RAR presentado. ${
                faltantes === 1 ? 'A 1 le falta' : `A ${faltantes} les falta`
            }.`
          : avisoRar
            ? `Hay RAR, pero ${
                  posteriores.length === 1 ? 'es posterior' : 'son posteriores'
              } a ${anioPeriodo}: no describen ese año.`
            : conRar !== null
              ? 'Todos los establecimientos tienen RAR presentado.'
              : paraContar.length === 1
                ? 'RAR del período.'
                : `${paraContar.length} RAR del período.`;

    return (
        <>
            <Box data-testid="nomina-celda">
                {/*
                 * `body2` y no `caption`: es el dato que se viene a buscar en
                 * esta celda. `caption` no está definido en el theme, cae al
                 * default de MUI y con el root en 14px termina en 10,5px, donde
                 * el gris se lava.
                 *
                 * En ámbar cuando hay algo que mirar: cero trabajadores, o
                 * expuestos que no van a viajar. El número solo, en gris, se
                 * leía como un dato más y no como el problema que es.
                 */}
                {/*
                 * Corto a propósito: "3 para enviar", no "3 trabajadores para
                 * enviar"; "1 expuesto", no "1 expuesto declarado". La columna
                 * se llama Período y la celda llegó a tener siete líneas; las
                 * palabras que sobran las repone el modal. Y la causa del cero
                 * va en la MISMA línea: "0 para enviar · 96 sin establecimiento"
                 * se lee de una, y es nómina sin asignar, no nómina vacía.
                 */}
                <Typography
                    variant="body2"
                    data-testid="nomina-trabajadores"
                    sx={{
                        fontWeight: 600,
                        color: conAviso ? colorAviso : 'text.primary'
                    }}
                >
                    {`${conAviso ? '⚠ ' : ''}${nomina.trabajadoresParaEnviar} para enviar`}
                    {sinTrabajadores && sinAsignar > 0 && (
                        <Box
                            component="span"
                            data-testid="nomina-sin-asignar"
                            sx={{ color: 'text.secondary', fontWeight: 400 }}
                        >
                            {` · ${sinAsignar} sin establecimiento`}
                        </Box>
                    )}
                </Typography>
                <Typography
                    variant="body2"
                    data-testid="nomina-expuestos"
                    sx={{ color: ajenos > 0 ? 'error.main' : 'text.secondary', display: 'block' }}
                >
                    {`${nomina.expuestosDeclarados} ${
                        nomina.expuestosDeclarados === 1 ? 'expuesto' : 'expuestos'
                    }${
                        ajenos > 0
                            ? ` · ${ajenos} de otro CUIT`
                            : hayFueraDeNomina
                              ? ` · ${nomina.expuestosFueraDeNomina} fuera de la nómina`
                              : ''
                    }`}
                </Typography>
            </Box>
            {/*
             * `DataTable/styles.module.css` pinta de blanco el hover del ÚLTIMO
             * botón de cada celda; la grilla lo deshace en el `TableContainer`
             * de `PresentacionesSVCCIndex` para los botones de esta celda.
             */}
            <Box display="flex" gap={0.75} mt={0.75} alignItems="center">
                <Tooltip title={tooltipRar}>
                    <IconButton
                        size="small"
                        onClick={() => setModalAbierto(true)}
                        aria-label={`${etiquetaBoton}${avisoRar ? ` (${avisoRar})` : ''} — CUIT ${cuit}`}
                        data-testid="nomina-ver"
                        sx={ESTILO_ICONO_CELDA(sinCobertura || sinRarAlCierre)}
                    >
                        <Badge badgeContent={contador} max={99}>
                            {sinNingunRar ? (
                                <GroupsOutlinedIcon fontSize="medium" />
                            ) : (
                                <PictureAsPdfOutlinedIcon fontSize="medium" />
                            )}
                        </Badge>
                    </IconButton>
                </Tooltip>
            </Box>
            {/*
             * Montado sólo mientras está abierto: si no, cada fila de la grilla
             * arrastraría un Dialog cerrado y su pedido del PDF.
             */}
            {modalAbierto && (
                <RarDelEstablecimientoDialog
                    abierto
                    onClose={() => setModalAbierto(false)}
                    cuit={cuit}
                    anioPeriodo={anioPeriodo}
                    rars={rars}
                    nomina={nomina}
                />
            )}
        </>
    );
};

interface PeriodoCeldaProps {
    fila: Pick<
        CuitPresentacionSvcc,
        | 'cuit'
        | 'anioPeriodo'
        | 'origenPeriodo'
        | 'nomina'
        | 'rarsDelEstablecimiento'
        | 'cantidadInformes'
        | 'cantidadEstablecimientos'
        | 'establecimientosConRar'
    >;
}

/**
 * Cuántos informes entran en el lote, en la misma línea que el año: "2025 · 1
 * informe". Tenía su propia línea y la celda llegó a siete.
 */
const CantidadInformes = ({ cantidad }: { cantidad: number }) => (
    <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
        {/* "informe SVCC", no "informe" a secas: el operador preguntó a qué se refería. */}
        {` · ${cantidad ?? 0} ${cantidad === 1 ? 'informe SVCC' : 'informes SVCC'}`}
    </Typography>
);

const PeriodoCelda = ({ fila }: PeriodoCeldaProps) => {
    if (fila.anioPeriodo === null) {
        return (
            <Box data-testid="periodo-sin-declarar">
                <Box display="flex" alignItems="baseline">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                        Sin período
                    </Typography>
                    <CantidadInformes cantidad={fila.cantidadInformes} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block' }}>
                    Asignalo para poder enviar
                </Typography>
                <NominaCelda cuit={fila.cuit} anioPeriodo={null} fila={fila} />
            </Box>
        );
    }

    return (
        <Box data-testid="periodo-celda">
            {/*
             * Hubo un chip "inferido" al lado del año y se sacó, por lo mismo
             * que el del RAR: en producción NINGÚN informe declara su período
             * hasta que se creen nuevos, así que la marca aparecía en casi
             * todas las filas. Una marca que está siempre no distingue nada, y
             * el operador no puede hacer nada distinto con ella.
             *
             * Encima su gris (`#f1f4f8`) es casi el fondo de la app (`#F3F8F8`),
             * así que el chip no se veía como chip: era texto flotando.
             *
             * El porqué no se perdió: vive en el tooltip del año.
             */}
            <Box display="flex" alignItems="baseline">
                <Tooltip
                    title={
                        fila.origenPeriodo === 'INFERIDO'
                            ? 'El informe no declara su período. Este año lo dedujimos de la fecha de presentación, o de cuándo se abrió el trámite en la SRT.'
                            : 'Período declarado por el informe al crearse.'
                    }
                >
                    <Typography
                        variant="body2"
                        data-testid="periodo-anio"
                        sx={{ fontWeight: 600, cursor: 'help', width: 'fit-content' }}
                    >
                        {fila.anioPeriodo}
                    </Typography>
                </Tooltip>
                <CantidadInformes cantidad={fila.cantidadInformes} />
            </Box>
            {/*
             * El vencimiento de la DDJJ se fue a la columna de Plazos, donde
             * convive con la purga del borrador. Acá amontonaba una quinta
             * línea en una celda que ya tenía cuatro, y dejaba a la columna de
             * Plazos vacía mientras no hubiera trámite abierto.
             */}
            <NominaCelda cuit={fila.cuit} anioPeriodo={fila.anioPeriodo} fila={fila} />
        </Box>
    );
};

export default PeriodoCelda;
